"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { monthsAndDaysBetween } from "@/lib/tool-kit/date-math/month-diff";

const MS_PER_DAY = 86_400_000;

interface Ymd {
  y: number;
  m: number; // 1-12
  d: number;
}

function parseYmd(value: string): Ymd | null {
  const parts = value.split("-");
  if (parts.length !== 3) {
    return null;
  }
  const y = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  const d = Number.parseInt(parts[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    return null;
  }
  return { y, m, d };
}

function toUtcMs(date: Ymd): number {
  return Date.UTC(date.y, date.m - 1, date.d);
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

interface AgeParts {
  years: number;
  months: number;
  days: number;
}

function calcAgeParts(dob: Ymd, asOf: Ymd): AgeParts | null {
  // Month-end convention (see month-diff.ts): a 31st missing from a short month borrows
  // that month's length, and the days are never negative.
  const diff = monthsAndDaysBetween(dob, asOf);
  if (!diff) {
    return null;
  }
  return {
    years: Math.floor(diff.months / 12),
    months: diff.months % 12,
    days: diff.days,
  };
}

function birthdayInYear(dob: Ymd, year: number): Ymd {
  // Feb 29 birthdays fall on Mar 1 in non-leap years (common convention)
  if (dob.m === 2 && dob.d === 29 && !isLeapYear(year)) {
    return { y: year, m: 3, d: 1 };
  }
  return { y: year, m: dob.m, d: dob.d };
}

interface NextBirthday {
  daysUntil: number;
  /** UTC midnight of the next birthday, for a localized weekday. */
  ms: number;
  date: string;
}

function calcNextBirthday(dob: Ymd, asOf: Ymd): NextBirthday {
  const asOfMs = toUtcMs(asOf);
  let candidate = birthdayInYear(dob, asOf.y);
  if (toUtcMs(candidate) < asOfMs) {
    candidate = birthdayInYear(dob, asOf.y + 1);
  }
  const candidateMs = toUtcMs(candidate);
  const daysUntil = Math.round((candidateMs - asOfMs) / MS_PER_DAY);
  const monthStr = String(candidate.m).padStart(2, "0");
  const dayStr = String(candidate.d).padStart(2, "0");
  return {
    daysUntil,
    ms: candidateMs,
    date: `${candidate.y}-${monthStr}-${dayStr}`,
  };
}

function todayYmd(): string {
  const now = new Date();
  const monthStr = String(now.getMonth() + 1).padStart(2, "0");
  const dayStr = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${monthStr}-${dayStr}`;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const AGE_CALCULATOR_STRINGS = {
  dateOfBirth: "Date of birth",
  nextBirthdayOn: "Next birthday ({date})",
  ageAsOfOptionalDefaults: "Age as of (optional, defaults to today)",
  theAsOfDateMust: 'The "as of" date must be on or after the date of birth.',
  years: "Years",
  months: "Months",
  days: "Days",
  totalDaysLived: "Total days lived",
  daysUntilNextBirthday: "Days until next birthday",
  /*
   * A frame plus three plural nouns, because the sentence counts three things.
   *
   * It was `{"Exact age: "}{years}{" years, "}{months}{" months, and "}{days}{"days. …"}`
   * — four fragments with no pluralisation at all, so English itself rendered "1 years,
   * 1 months, and 1 days". One plural message cannot carry three independent counts, so
   * each noun is its own message and the frame places them; that also lets a language
   * reorder the three, which a chain of fragments cannot.
   */
  exactAge: "Exact age: {years}, {months}, and {days}.",
  yearsCount: "one: {count} year|other: {count} years",
  monthsCount: "one: {count} month|other: {count} months",
  daysCount: "one: {count} day|other: {count} days",
  breakdownNote:
    "The breakdown counts complete years first, then complete months, then remaining days using real calendar month lengths.",
} as const;

export default function AgeCalculatorWidget() {
  const [dobInput, setDobInput] = useState("");
  const [asOfInput, setAsOfInput] = useState("");

  const dob = parseYmd(dobInput);
  const asOf = parseYmd(asOfInput.length > 0 ? asOfInput : todayYmd());

  const age = dob && asOf ? calcAgeParts(dob, asOf) : null;
  const totalDays =
    dob && asOf && age
      ? Math.round((toUtcMs(asOf) - toUtcMs(dob)) / MS_PER_DAY)
      : null;
  const nextBirthday = dob && asOf && age ? calcNextBirthday(dob, asOf) : null;

  const strings = useToolUi(AGE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const formatNum = useToolNumber();

  return (
    <>
      {/* Inputs */}
      <section className="flex flex-wrap items-end gap-4 rounded-lg border border-border bg-card p-6">
        <div className="space-y-1">
          <label
            className="block font-medium text-foreground text-xs"
            htmlFor="dob-input"
          >
            {strings.dateOfBirth}
          </label>
          <input
            className="rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="dob-input"
            onChange={(e) => setDobInput(e.target.value)}
            type="date"
            value={dobInput}
          />
        </div>
        <div className="space-y-1">
          <label
            className="block font-medium text-foreground text-xs"
            htmlFor="asof-input"
          >
            {strings.ageAsOfOptionalDefaults}
          </label>
          <input
            className="rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="asof-input"
            onChange={(e) => setAsOfInput(e.target.value)}
            type="date"
            value={asOfInput}
          />
        </div>
      </section>
      {dob && !age && (
        <p className="text-muted-foreground text-sm">
          {strings.theAsOfDateMust}
        </p>
      )}
      {/* Results */}
      {age && totalDays !== null && nextBirthday && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard label={strings.years} value={age.years} />
            <StatCard label={strings.months} value={age.months} />
            <StatCard label={strings.days} value={age.days} />
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label={strings.totalDaysLived}
              value={formatNum(totalDays)}
            />
            <StatCard
              label={strings.daysUntilNextBirthday}
              value={nextBirthday.daysUntil}
            />
            <StatCard
              label={t(strings.nextBirthdayOn, { date: nextBirthday.date })}
              value={new Intl.DateTimeFormat(locale, {
                weekday: "long",
                timeZone: "UTC",
              }).format(nextBirthday.ms)}
            />
          </div>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {t(strings.exactAge, {
              years: tPlural(strings.yearsCount, locale, age.years).trim(),
              months: tPlural(strings.monthsCount, locale, age.months).trim(),
              days: tPlural(strings.daysCount, locale, age.days).trim(),
            })}{" "}
            {strings.breakdownNote}
          </p>
        </>
      )}
    </>
  );
}
